import React, { useState } from 'react';
import {
  Box, VStack, HStack, Flex, Heading, Text, Button, SimpleGrid, Card, CardHeader,
  CardBody, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, FormControl,
  FormLabel, Input, Select, Textarea, Checkbox, Divider, Badge, useToast, Link,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { MdVolunteerActivism, MdMyLocation, MdAttachFile, MdPhone } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/AxiosInstance';

const initialForm = {
  nombre: '', telefono: '', email: '',
  tipoApoyo: '', situacion: '',
  estado: 'Tlaxcala', municipio: 'Totolac', localidad: '', colonia: '',
  vialidad: '', numeroExterior: '', referencias: '',
  latitud: '', longitud: '', urlMaps: '',
  fecha: '', hora: '', disponibilidad: '',
};

const TIPOS_APOYO = [
  'Asesoría jurídica', 'Atención psicológica', 'Acompañamiento', 'Trabajo social',
  'Canalización', 'Orden de protección', 'Otro',
];

const SolicitudAuxilioIMMPublico: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({ ...initialForm });
  const [atencionInmediata, setAtencionInmediata] = useState(false);
  const [fotos, setFotos] = useState<File[]>([]);
  const [aviso, setAviso] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState('');

  const setField = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const obtenerUbicacion = () => {
    if (!navigator.geolocation) {
      toast({ title: 'La geolocalización no está disponible en este dispositivo', status: 'warning' });
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setForm((f) => ({
          ...f,
          latitud: String(lat),
          longitud: String(lng),
          urlMaps: `https://www.google.com/maps?q=${lat},${lng}`,
        }));
        setGeoLoading(false);
        toast({ title: 'Ubicación obtenida', status: 'success', duration: 2000 });
      },
      () => {
        setGeoLoading(false);
        toast({ title: 'No se pudo obtener la ubicación', status: 'error' });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const validar = (): string | null => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio.';
    if (!form.telefono.trim()) return 'El teléfono de contacto es obligatorio.';
    if (form.telefono && !/^\d{7,15}$/.test(form.telefono.replace(/\D/g, ''))) return 'El teléfono no es válido.';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'El correo electrónico no es válido.';
    if (!form.situacion.trim()) return 'Describa brevemente su situación.';
    if (!aviso) return 'Debe aceptar el aviso de privacidad para continuar.';
    return null;
  };

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setFotos(files);
  };

  const handleSubmit = async () => {
    const err = validar();
    if (err) {
      setError(err);
      toast({ title: err, status: 'warning', duration: 3000 });
      return;
    }
    setError('');
    setEnviando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) fd.append(k, String(v));
      });
      fd.append('atencionInmediata', atencionInmediata ? 'true' : 'false');
      fotos.forEach((f) => fd.append('archivos', f));

      const { data } = await axiosInstance.post('/imm/solicitudes/publica', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e.message || 'No se pudo enviar la solicitud';
      setError(msg);
      toast({ title: msg, status: 'error', duration: 4000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdVolunteerActivism} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Solicitud de Auxilio</Heading>
            <Text fontSize="sm" opacity={0.85}>Instancia Municipal de la Mujer</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={8} px={{ base: 4, md: 10 }} maxW="820px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" wrap="wrap">
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500"
              onClick={() => navigate('/portal/2026')}>
              Portal Ciudadano
            </Button>
          </HStack>

          <Alert status="warning" borderRadius="md" variant="left-accent">
            <AlertIcon as={MdPhone} />
            <Box>
              <AlertTitle fontSize="sm">¿Es una emergencia?</AlertTitle>
              <AlertDescription fontSize="sm">
                Si su vida o integridad están en peligro, llame de inmediato al <b>911</b>.
                Este formulario no es un canal de atención inmediata de emergencias.
              </AlertDescription>
            </Box>
          </Alert>

          {resultado ? (
            <Card shadow="md">
              <CardBody textAlign="center" py={10}>
                <Icon as={CheckCircleIcon} boxSize={14} color="green.400" mb={4} />
                <Heading size="md" color="green.600">Solicitud enviada</Heading>
                <Text color="gray.600" mt={2}>
                  {resultado.mensaje || 'Su solicitud fue recibida. La Instancia Municipal de la Mujer se pondrá en contacto.'}
                </Text>
                <Box mt={5} p={4} bg="gray.50" borderRadius="md" display="inline-block">
                  <Text fontSize="xs" color="gray.500">FOLIO DE SEGUIMIENTO</Text>
                  <Text fontFamily="mono" fontWeight="bold" fontSize="2xl" color="#5b182b">
                    {resultado.folio || '—'}
                  </Text>
                  {resultado.estatus && (
                    <Badge mt={2} colorScheme="pink">{resultado.estatus}</Badge>
                  )}
                </Box>
                <Alert status="info" borderRadius="md" mt={5} textAlign="left">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Se ha enviado un correo de confirmación con su folio. Consérvelo para dar seguimiento.
                  </Text>
                </Alert>
                <HStack justify="center" spacing={3} mt={6}>
                  <Button variant="outline" size="sm" onClick={() => navigate('/portal/2026')}>Ir al Portal</Button>
                </HStack>
              </CardBody>
            </Card>
          ) : (
            <Card shadow="md">
              <CardHeader bg="gray.50" borderTopRadius="md">
                <Heading size="sm" color="#5b182b">Datos de la solicitud</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  La información que proporcione es confidencial. Los campos con * son obligatorios.
                </Text>
              </CardHeader>
              <CardBody>
                <VStack spacing={5} align="stretch">
                  {error && <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Nombre completo</FormLabel>
                      <Input value={form.nombre} onChange={(e) => setField('nombre', e.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Teléfono de contacto</FormLabel>
                      <Input value={form.telefono} onChange={(e) => setField('telefono', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                      <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                      <Text fontSize="xs" color="gray.400" mt={1}>Se enviará su folio a este correo.</Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Tipo de apoyo</FormLabel>
                      <Select placeholder="Seleccione" value={form.tipoApoyo}
                        onChange={(e) => setField('tipoApoyo', e.target.value)}>
                        {TIPOS_APOYO.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Describa su situación</FormLabel>
                    <Textarea rows={4} value={form.situacion}
                      onChange={(e) => setField('situacion', e.target.value)}
                      placeholder="Cuéntenos brevemente en qué podemos ayudarle." />
                  </FormControl>

                  <Checkbox colorScheme="pink" isChecked={atencionInmediata}
                    onChange={(e) => setAtencionInmediata(e.target.checked)}>
                    Requiero atención lo antes posible
                  </Checkbox>

                  <FormControl>
                    <FormLabel fontSize="sm">Disponibilidad para ser contactada(o)</FormLabel>
                    <Input value={form.disponibilidad} onChange={(e) => setField('disponibilidad', e.target.value)}
                      placeholder="Ej. Lunes a viernes por la mañana" />
                  </FormControl>

                  <Divider />
                  <Text fontSize="sm" fontWeight="bold" color="gray.600">Ubicación (opcional)</Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Localidad</FormLabel>
                      <Input value={form.localidad} onChange={(e) => setField('localidad', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Colonia</FormLabel>
                      <Input value={form.colonia} onChange={(e) => setField('colonia', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Calle / vialidad</FormLabel>
                      <Input value={form.vialidad} onChange={(e) => setField('vialidad', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Número exterior</FormLabel>
                      <Input value={form.numeroExterior} onChange={(e) => setField('numeroExterior', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Referencias</FormLabel>
                      <Input value={form.referencias} onChange={(e) => setField('referencias', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <HStack>
                    <Button size="sm" variant="outline" colorScheme="pink" leftIcon={<Icon as={MdMyLocation} />}
                      onClick={obtenerUbicacion} isLoading={geoLoading} loadingText="Obteniendo…">
                      Usar mi ubicación actual
                    </Button>
                    {form.urlMaps && (
                      <Link href={form.urlMaps} isExternal fontSize="sm" color="blue.600">Ver en el mapa</Link>
                    )}
                  </HStack>

                  <Divider />
                  <Text fontSize="sm" fontWeight="bold" color="gray.600">Cita / disponibilidad (opcional)</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Fecha preferente</FormLabel>
                      <Input type="date" value={form.fecha} onChange={(e) => setField('fecha', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Hora preferente</FormLabel>
                      <Input type="time" value={form.hora} onChange={(e) => setField('hora', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel fontSize="sm">
                      <HStack><Icon as={MdAttachFile} /><Text>Fotos o documentos (opcional, máx. 5)</Text></HStack>
                    </FormLabel>
                    <Input type="file" multiple accept="image/*,application/pdf" onChange={handleFiles}
                      p={1} border="none" />
                    {fotos.length > 0 && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {fotos.length} archivo(s) seleccionado(s): {fotos.map((f) => f.name).join(', ')}
                      </Text>
                    )}
                  </FormControl>

                  <Divider />
                  {/* Aviso de privacidad */}
                  <Box bg="gray.50" p={4} borderRadius="md" fontSize="xs" color="gray.600">
                    <Text fontWeight="bold" mb={1}>Aviso de privacidad</Text>
                    <Text>
                      Los datos personales que proporcione serán tratados de forma confidencial por la
                      Instancia Municipal de la Mujer del H. Ayuntamiento de Totolac, con la única finalidad
                      de brindarle atención, acompañamiento y canalización. No se compartirán con terceros
                      sin su consentimiento, salvo obligación legal.
                    </Text>
                  </Box>
                  <Checkbox colorScheme="pink" isChecked={aviso} onChange={(e) => setAviso(e.target.checked)}>
                    <Text fontSize="sm">He leído y acepto el aviso de privacidad.</Text>
                  </Checkbox>

                  <Button colorScheme="pink" size="lg" onClick={handleSubmit} isLoading={enviando}
                    loadingText="Enviando…" leftIcon={<Icon as={MdVolunteerActivism} />}>
                    Enviar solicitud
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Instancia Municipal de la Mujer · H. Ayuntamiento de Totolac · Tlaxcala
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default SolicitudAuxilioIMMPublico;
